import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';

import { useProjects } from '@/hooks/useProjects';
import type { Project } from '@/types/project';

/** Which per-system mapping an option needs before it's selectable. */
export type ProjectRequirement = 'sentry' | 'datadog';

interface ProjectFilterProps {
  /** Selected project_key, or '' for "all". */
  value: string;
  onChange: (projectKey: string, project: Project | null) => void;
  /**
   * When set, a project lacking that mapping renders disabled rather than
   * hidden — "threadr exists but has no Sentry projects mapped" is useful
   * information, and picking it could only ever return nothing.
   *
   * Omit it when every project must stay selectable. /investigate does: only
   * one repo has Datadog services today, so gating on `datadog` there would
   * leave a mandatory field with 8 of 9 options dead and no way to submit.
   */
  requires?: ProjectRequirement;
  label?: string;
  disabled?: boolean;
  /** Label for the no-filter option. Omit to make a selection required. */
  allLabel?: string;
  /** Renders the asterisk + sets aria-required so a blocked submit is explained. */
  required?: boolean;
  sx?: object;
}

function isUnmapped(project: Project, requires: ProjectRequirement | undefined): boolean {
  if (requires === undefined) return false;
  const mapping =
    requires === 'sentry' ? project.sentry_project_slugs : project.datadog_services;
  return mapping.length === 0;
}

/**
 * Single-select project filter backed by the shared registry
 * (`GET /api/projects/`).
 *
 * Used by /issues (resolves to Sentry project slugs server-side) and
 * /investigate (fills the Datadog services scope). The intake wizard has its
 * own multi-select over DetectOS *products* — a different granularity, so
 * deliberately not this component.
 */
export function ProjectFilter({
  value,
  onChange,
  requires,
  label = 'Project',
  disabled,
  allLabel,
  required,
  sx,
}: ProjectFilterProps): ReactElement {
  const { projects, isLoading, error } = useProjects();

  const helperText = error
    ? `Couldn't load projects: ${error.message}`
    : !isLoading && projects.length === 0
      ? 'No projects registered yet'
      : required && value === ''
        ? 'Pick a project to continue'
        : undefined;

  return (
    <TextField
      select
      id="project-filter"
      label={label}
      value={value}
      onChange={(e) => {
        const key = e.target.value;
        onChange(key, projects.find((p) => p.project_key === key) ?? null);
      }}
      size="small"
      required={required}
      disabled={disabled || isLoading || Boolean(error)}
      error={Boolean(error)}
      helperText={helperText}
      sx={{ minWidth: 200, ...sx }}
      // MUI renders a native-ish select; the label association is automatic
      // via `label` + `id`, so no extra aria-label is needed here.
      SelectProps={{ MenuProps: { 'aria-label': 'Project filter options' } }}
    >
      {allLabel !== undefined && (
        <MenuItem value="">
          <em>{allLabel}</em>
        </MenuItem>
      )}
      {projects.map((p) => {
        const unmapped = isUnmapped(p, requires);
        return (
          <MenuItem key={p.project_key} value={p.project_key} disabled={unmapped}>
            {p.project_key}
            {unmapped ? ` — no ${requires === 'sentry' ? 'Sentry' : 'Datadog'} mapping` : ''}
          </MenuItem>
        );
      })}
    </TextField>
  );
}
